/**
 * Vue transversale de maintenance : une ligne par GRV sur l'ensemble de la flotte
 * (`GET /v1/maintenance`, D59), avec recherche, filtre et tri — la fiche d'un GRV garde en
 * parallèle son propre historique complet (onglets « Fiche de vie » et « Check-list »).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Chargement, Erreur, Vide } from "../../shared/composants/Etats";
import { EnTeteTriable, useTrie, useTriTableau } from "../../shared/composants/tableauTriable";
import { formaterDateSeule } from "../../shared/composants/formatage";
import { BoutonExporterCsv } from "../../shared/composants/exportCsv";
import type { LigneMaintenanceTransversale } from "../../shared/api/types";
import { useVueTransversale } from "./requetes";

type Colonne =
  | "numero_grv"
  | "statut_operationnel"
  | "dernier_evenement_date"
  | "derniere_checklist_date"
  | "date_prochaine_epreuve_etancheite";

function valeurDe(
  ligne: LigneMaintenanceTransversale,
  colonne: Colonne,
): string | null | undefined {
  return ligne[colonne];
}

function texteRecherchable(ligne: LigneMaintenanceTransversale): string {
  return [
    ligne.numero_grv,
    ligne.statut_operationnel,
    ligne.dernier_evenement_type,
    ligne.dernier_evenement_detail,
    ligne.derniere_checklist_statut,
    ligne.date_prochaine_epreuve_etancheite,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function EcranMaintenance() {
  const [client, definirClient] = useState("");
  const [recherche, definirRecherche] = useState("");
  const { tri, basculerTri } = useTriTableau<Colonne>("numero_grv");

  const requete = useVueTransversale({ client: client || undefined });
  const lignes = useMemo(() => requete.data?.data ?? [], [requete.data]);

  const filtrees = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    if (!terme) return lignes;
    return lignes.filter((ligne) => texteRecherchable(ligne).includes(terme));
  }, [lignes, recherche]);
  const triees = useTrie(filtrees, tri, valeurDe);

  return (
    <section>
      <h1>Maintenance</h1>
      <p className="carte__description">
        Vue transversale sur l'ensemble de la flotte. La fiche de vie et les check-lists complètes
        d'un GRV restent consultables depuis sa fiche.
      </p>

      <div className="barre-de-filtres">
        <label htmlFor="filtre-maintenance-client">Client</label>
        <input
          id="filtre-maintenance-client"
          type="text"
          placeholder="tous"
          value={client}
          onChange={(e) => definirClient(e.target.value)}
        />
        <label htmlFor="recherche-maintenance">Rechercher</label>
        <input
          id="recherche-maintenance"
          type="search"
          placeholder="GRV, statut, événement, check-list, échéance…"
          value={recherche}
          onChange={(e) => definirRecherche(e.target.value)}
        />

        <BoutonExporterCsv
          nomFichier="maintenance"
          colonnes={[
            { libelle: "GRV", valeur: (l: LigneMaintenanceTransversale) => l.numero_grv },
            {
              libelle: "Statut",
              valeur: (l: LigneMaintenanceTransversale) => l.statut_operationnel,
            },
            {
              libelle: "Dernier événement",
              valeur: (l: LigneMaintenanceTransversale) => l.dernier_evenement_type,
            },
            {
              libelle: "Date du dernier événement",
              valeur: (l: LigneMaintenanceTransversale) =>
                l.dernier_evenement_date ? formaterDateSeule(l.dernier_evenement_date) : "",
            },
            {
              libelle: "Dernière check-list",
              valeur: (l: LigneMaintenanceTransversale) => l.derniere_checklist_statut,
            },
            {
              libelle: "Date de la dernière check-list",
              valeur: (l: LigneMaintenanceTransversale) =>
                l.derniere_checklist_date ? formaterDateSeule(l.derniere_checklist_date) : "",
            },
            {
              libelle: "Prochaine épreuve d'étanchéité",
              valeur: (l: LigneMaintenanceTransversale) => l.date_prochaine_epreuve_etancheite,
            },
          ]}
          lignes={triees}
        />
      </div>

      {requete.isPending && <Chargement libelle="Chargement de la vue transversale…" />}
      {requete.isError && <Erreur erreur={requete.error} reessayer={() => void requete.refetch()} />}
      {requete.data && triees.length === 0 && (
        <Vide libelle="Aucun GRV ne correspond à ces critères." />
      )}

      {triees.length > 0 && (
        <table className="tableau">
          <thead>
            <tr>
              <EnTeteTriable colonne="numero_grv" libelle="GRV" tri={tri} basculerTri={basculerTri} />
              <EnTeteTriable
                colonne="statut_operationnel"
                libelle="Statut"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="dernier_evenement_date"
                libelle="Dernier événement"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="derniere_checklist_date"
                libelle="Dernière check-list"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="date_prochaine_epreuve_etancheite"
                libelle="Prochaine épreuve d'étanchéité"
                tri={tri}
                basculerTri={basculerTri}
              />
            </tr>
          </thead>
          <tbody>
            {triees.map((ligne) => (
              <tr key={ligne.numero_grv}>
                <td>
                  <Link to={`/grv/${encodeURIComponent(ligne.numero_grv)}`}>{ligne.numero_grv}</Link>
                </td>
                <td>
                  <span className="badge">{ligne.statut_operationnel}</span>
                </td>
                <td>
                  {ligne.dernier_evenement_type ? (
                    <>
                      {ligne.dernier_evenement_type}
                      <br />
                      <small>{formaterDateSeule(ligne.dernier_evenement_date!)}</small>
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  {ligne.derniere_checklist_statut ? (
                    <>
                      {ligne.derniere_checklist_statut}
                      <br />
                      <small>{formaterDateSeule(ligne.derniere_checklist_date!)}</small>
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td>{ligne.date_prochaine_epreuve_etancheite ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
